/* eslint-disable @typescript-eslint/no-explicit-any */
import { Empty } from 'antd'
import { FC } from 'react'
import Table from '../Table'
import CustomizedPlot from '../CustomizedPlot'
import DownloadableImage from '../DownloadableImage'
import SafeMarkdown from '../SafeMarkdown'
import { AnswerTypes, Props } from './types'
import { helpers } from '../../helpers'
import { StyledAnswerRendererContainer } from './index.styles'
import { IMAGE_STORE_BASE_URL, MIXED_RENDER_MAX_DEPTH, PROCESS_ERROR_MESSAGE } from '../../constants'
import 'verigor-snail-chart'

const AnswerRenderer: FC<Props> = ({
    inProgress = false,
    answer,
    resultType,
    resultStatus,
    limitedTableHeight = true,
    sortedTableColumns,
    showTableFixingColumnIcon = true,
    isFullScreen,
    shouldRenderSuggestions = false,
    suggestions = [],
    imageStoreBaseUrl = IMAGE_STORE_BASE_URL,
    onSuggestionClick,
}) => {
    const parsedSuggestions = helpers.parseSuggestions(suggestions)
    const hasAnswer = helpers.hasDisplayableAnswer(answer)
    const preparedMixed = resultType === AnswerTypes.Mixed ? helpers.normalizeMixedAnswers(answer) : undefined

    const render = (type: string | undefined, resultAnswer: string | number | any[] | undefined, options?: { emptyDescription?: string; depth?: number }) => {
        const depth = options?.depth ?? 0
        const answerType = helpers.getAnswerType(resultAnswer, type)

        try {
            if (answerType === AnswerTypes.StringifiedArray) {
                return (
                    <Table
                        items={JSON.parse(resultAnswer as string) as any[]}
                        limitedHeight={limitedTableHeight}
                        sortedColumns={sortedTableColumns}
                        showFixingColumnIcon={showTableFixingColumnIcon}
                    />
                )
            } else if (answerType === AnswerTypes.Dataframe) {
                let items: any[] = []
                let emptyDescription = options?.emptyDescription

                if (Array.isArray(resultAnswer)) {
                    items = resultAnswer
                } else if (typeof resultAnswer === 'string') {
                    const parsed = JSON.parse(resultAnswer)
                    items = Array.isArray(parsed?.value) ? parsed.value : []
                    emptyDescription = parsed?.message ?? (parsed?.value?.result !== undefined ? String(parsed.value.result) : emptyDescription)
                } else if (typeof resultAnswer === 'object' && resultAnswer !== null) {
                    const resultObject = resultAnswer as any
                    items = Array.isArray(resultObject?.value) ? resultObject.value : []
                    emptyDescription = resultObject?.message ?? (resultObject?.value?.result !== undefined ? String(resultObject.value.result) : emptyDescription)
                }

                return (
                    <Table
                        items={items}
                        limitedHeight={limitedTableHeight}
                        sortedColumns={sortedTableColumns}
                        showFixingColumnIcon={showTableFixingColumnIcon}
                        emptyDescription={emptyDescription}
                    />
                )
            } else if (answerType === AnswerTypes.Array) {
                return (
                    <Table
                        items={resultAnswer as any[]}
                        limitedHeight={limitedTableHeight}
                        sortedColumns={sortedTableColumns}
                        showFixingColumnIcon={showTableFixingColumnIcon}
                    />
                )
            } else if (answerType === AnswerTypes.Image) {
                return <DownloadableImage src={helpers.buildImageUrl(resultAnswer, imageStoreBaseUrl)} alt="query_img" style={{ width: '100%' }} />
            } else if (answerType === AnswerTypes.Chart) {
                const plotProps = typeof resultAnswer === 'string' ? JSON.parse(resultAnswer) : resultAnswer
                if (!plotProps || typeof plotProps !== 'object') {
                    throw new Error('Invalid chart payload')
                }
                return <CustomizedPlot {...plotProps} />
            } else if (answerType === AnswerTypes.Snail) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', width: '100%' }}>
                        <div style={{ width: isFullScreen ? '75vh' : '60vh' }}>
                            <verigor-snail-chart value={resultAnswer?.toString()} language="tr"></verigor-snail-chart>
                        </div>
                    </div>
                )
            } else if (answerType === AnswerTypes.Mixed) {
                if (depth >= MIXED_RENDER_MAX_DEPTH) {
                    return <Empty description={PROCESS_ERROR_MESSAGE} />
                }
                const mixedAnswers = depth === 0 && preparedMixed !== undefined
                    ? preparedMixed
                    : helpers.normalizeMixedAnswers(resultAnswer)
                if (!mixedAnswers) {
                    return <Empty description={PROCESS_ERROR_MESSAGE} />
                }
                return (
                    <div className="mixed-answer-container">
                        {mixedAnswers.map((mixedAnswer, index) => (
                            <div key={index} className="mixed-answer-item">
                                {mixedAnswer.description && (
                                    <div className="mixed-answer-description">
                                        <SafeMarkdown markdown={mixedAnswer.description} />
                                    </div>
                                )}
                                {mixedAnswer.data && (
                                    <div className="mixed-answer-data">
                                        {render(mixedAnswer.data.type, mixedAnswer.data.value, {
                                            emptyDescription: mixedAnswer.data.message,
                                            depth: depth + 1,
                                        })}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )
            } else {
                const displayText = helpers.toDisplayText(resultAnswer)
                return <div className="answer-string">{displayText || <Empty />}</div>
            }
        } catch (error) {
            console.log(error)
            return <Empty description={PROCESS_ERROR_MESSAGE} />
        }
    }

    const renderSuggestions = () => {
        if (!inProgress && shouldRenderSuggestions && parsedSuggestions.length > 0) {
            return (
                <div className="answer-suggestions">
                    {parsedSuggestions.map((suggestion, index) => (
                        <button
                            type="button"
                            key={index}
                            className="answer-suggestion-item"
                            onClick={() => onSuggestionClick?.(suggestion)}
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )
        }
        return null
    }

    const renderFailed = () => <Empty description={PROCESS_ERROR_MESSAGE} />
    const mixedInvalid = resultType === AnswerTypes.Mixed && preparedMixed == null
    const failedExecution = !inProgress && resultStatus === false && !hasAnswer
    const shouldShowFailed = mixedInvalid || failedExecution

    return (
        <StyledAnswerRendererContainer className="answer-renderer-container">
            {shouldShowFailed ? renderFailed() : hasAnswer ? render(resultType, answer ?? undefined) : null}
            {renderSuggestions()}
        </StyledAnswerRendererContainer>
    )
}

export default AnswerRenderer
